// src/pages/EmployeesPage/EmployeesPage.jsx
import React, { useEffect, useState, useRef } from "react";
import "./EmployeesPage.css";

const API_BASE = process.env.REACT_APP_API_BASE || `${process.env.REACT_APP_API_URL || ""}`;

function apiHeaders(json = true) {
  const headers = {};
  if (json) headers["Content-Type"] = "application/json";
  const token = localStorage.getItem("token");
  if (token) headers["Authorization"] = `Bearer ${token}`;
  return headers;
}

export default function EmployeesPage() {
  const [employees, setEmployees] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [query, setQuery] = useState("");
  const timeoutRef = useRef(null);

  // Modal state: selected employee + their tasks
  const [selectedEmployee, setSelectedEmployee] = useState(null);
  const [modalOpen, setModalOpen] = useState(false);
  const [empLoading, setEmpLoading] = useState(false);
  const [empError, setEmpError] = useState("");
  const [empTasks, setEmpTasks] = useState([]);

  function showMsg(text) {
    setError(text);
    if (timeoutRef.current) clearTimeout(timeoutRef.current);
    timeoutRef.current = setTimeout(() => setError(""), 4000);
  }

  async function loadEmployees() {
    setLoading(true);
    setError("");
    try {
      const res = await fetch(`${API_BASE}/api/employees`, {
        headers: apiHeaders(false),
      });
      if (!res.ok) {
        const txt = await res.text().catch(() => "");
        throw new Error(txt || `Failed to load employees (${res.status})`);
      }
      const data = await res.json();
      setEmployees(Array.isArray(data) ? data : []);
    } catch (err) {
      console.error("loadEmployees error:", err);
      setEmployees([]);
      showMsg(err.message || "Failed to load employees");
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    loadEmployees();
    return () => {
      if (timeoutRef.current) clearTimeout(timeoutRef.current);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Filtered view
  const filtered = employees.filter((emp) => {
    const q = query.trim().toLowerCase();
    if (!q) return true;
    return (
      (emp.name || "").toString().toLowerCase().includes(q) ||
      (emp.email || "").toString().toLowerCase().includes(q) ||
      (emp.position || "").toString().toLowerCase().includes(q)
    );
  });

  // -------------------- Task fetching (best-effort) --------------------
  // Tries multiple possible backend routes to fetch tasks for an employee.
  async function fetchEmployeeTasks(employeeId) {
    const tries = [
      // common guess (you used this earlier and got 404)
      `${API_BASE}/api/tasks/employee/${employeeId}`,
      // alternative query param style
      `${API_BASE}/api/tasks?employee_id=${employeeId}`,
      `${API_BASE}/api/tasks?assigned_to=${employeeId}`,
      // task-assignments based attempts (returns assignments; we map to tasks minimally)
      `${API_BASE}/api/task-assignments?employee_id=${employeeId}`,
      // fallback: tasks/my (may require token to represent employee)
      `${API_BASE}/api/tasks/my`,
    ];

    for (const url of tries) {
      try {
        const res = await fetch(url, {
          headers: apiHeaders(false),
        });

        // If 404/Not Found try next
        if (res.status === 404) {
          continue;
        }

        // If server refused or CORS or network error will throw before here
        const ct = res.headers.get("content-type") || "";
        let body;
        if (ct.includes("application/json")) {
          body = await res.json();
        } else {
          // if it's not JSON, read text and treat as failure
          const text = await res.text().catch(() => "");
          // treat non-JSON as failure unless it's empty array-ish
          if (!text) body = [];
          else throw new Error(text);
        }

        // Normalize different shapes:
        // - tasks array shape: [{ id, title, status, ... }]
        // - task-assignments endpoint may return assignment rows with task_id + fields
        if (!Array.isArray(body)) {
          // some endpoints may return object with data field
          if (body && Array.isArray(body.data)) body = body.data;
          else body = [];
        }

        // If this looks like assignments (has task_id) convert to task-lite objects
        if (body.length && body[0] && body[0].task_id && !body[0].id) {
          // map assignment rows to minimal tasks (we'll try to include assignment.status)
          const mapped = body.map((a) => ({
            id: a.task_id,
            title: a.title || a.task_title || `Task ${a.task_id}`,
            status: a.status || a.assignment_status || "todo",
            assigned_at: a.assigned_at,
            assignment: a,
            // keep raw assignment row too
            _raw: a,
          }));
          return mapped;
        }

        // If body is tasks already
        return body;
      } catch (err) {
        // network / parse errors — try next route but log
        console.warn(`fetchEmployeeTasks try failed for ${url}:`, err);
        continue;
      }
    }

    // nothing worked
    throw new Error(
      "No task endpoint responded for this employee. Backend may not expose tasks-by-employee route."
    );
  }

  // -------------------- Open employee modal --------------------
  async function openEmployeeModal(emp) {
    setSelectedEmployee(emp);
    setModalOpen(true);
    setEmpLoading(true);
    setEmpError("");
    setEmpTasks([]);

    try {
      // fetch employee details (fresh)
      const empRes = await fetch(`${API_BASE}/api/employees/${emp.id}`, {
        headers: apiHeaders(false),
      });
      if (empRes.ok) {
        const empBody = await empRes.json().catch(() => null);
        if (empBody) setSelectedEmployee(empBody);
      }

      // fetch tasks (best-effort)
      const tasks = await fetchEmployeeTasks(emp.id);

      // split tasks into current vs completed by looking for 'done' / 'completed'
      const isDone = (t) => {
        const s = (t.status || t._raw?.status || t.assignment?.status || "")
          .toString()
          .toLowerCase();
        return s === "done" || s === "completed";
      };

      const current = [];
      const done = [];

      (Array.isArray(tasks) ? tasks : []).forEach((t) => {
        if (isDone(t)) done.push(t);
        else current.push(t);
      });

      // sort current by due_date ascending if present; done by updated_at desc
      current.sort((a, b) => {
        const da = a.due_date ? new Date(a.due_date).getTime() : Infinity;
        const db = b.due_date ? new Date(b.due_date).getTime() : Infinity;
        return da - db;
      });
      done.sort((a, b) => {
        const da = a.updated_at ? new Date(a.updated_at).getTime() : 0;
        const db = b.updated_at ? new Date(b.updated_at).getTime() : 0;
        return db - da;
      });

      setEmpTasks({ current, done });
    } catch (err) {
      console.error("loadEmployeeTasks error:", err);
      setEmpError(err.message || "Failed to load employee tasks");
      setEmpTasks({ current: [], done: [] });
    } finally {
      setEmpLoading(false);
    }
  }

  function closeModal() {
    setModalOpen(false);
    setSelectedEmployee(null);
    setEmpTasks([]);
    setEmpError("");
  }

  // -------------- JSX --------------
  return (
    <div className="employees-page">
      <header className="employees-header">
        <div>
          <h2>Employees</h2>
          <p className="sub">List of all employees in the organization (view only)</p>
        </div>

        <div className="employees-controls">
          <input
            className="search"
            placeholder="Search by name, email or position…"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
          />
          <button className="btn" onClick={loadEmployees} disabled={loading}>
            Refresh
          </button>
        </div>
      </header>

      {error && <div className="employees-error">{error}</div>}

      <section className="employees-list">
        <div className="table-wrapper">
          <table className="employees-table" role="table" aria-label="Employees">
            <thead>
              <tr>
                <th>ID</th>
                <th>Name</th>
                <th>Email</th>
                <th>Position</th>
                <th>Salary</th>
                <th>Hire Date</th>
              </tr>
            </thead>

            <tbody>
              {loading ? (
                <tr>
                  <td colSpan="6" className="empty">Loading…</td>
                </tr>
              ) : filtered.length === 0 ? (
                <tr>
                  <td colSpan="6" className="empty">No employees found</td>
                </tr>
              ) : (
                filtered.map((emp) => (
                  <tr
                    key={emp.id}
                    onClick={() => openEmployeeModal(emp)}
                    style={{ cursor: "pointer" }}
                    title="Click to view tasks & details"
                  >
                    <td>{emp.id}</td>
                    <td>{emp.name}</td>
                    <td>{emp.email}</td>
                    <td>{emp.position || "—"}</td>
                    <td>{emp.salary != null ? Number(emp.salary).toFixed(2) : "—"}</td>
                    <td>{emp.hire_date ? new Date(emp.hire_date).toLocaleDateString() : "—"}</td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </section>

      {/* -------- Employee Modal -------- */}
      {modalOpen && selectedEmployee && (
        <div className="employees-modal-overlay" onMouseDown={closeModal}>
          <div className="employees-modal" onMouseDown={(e) => e.stopPropagation()} role="dialog" aria-modal="true">
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", gap: 12 }}>
              <div>
                <h3>{selectedEmployee.name || "Employee"}</h3>
                <div style={{ color: "#475569", fontSize: 13 }}>
                  {selectedEmployee.position ? `${selectedEmployee.position} · ` : ""}
                  {selectedEmployee.email}
                </div>
                <div style={{ marginTop: 8, color: "#6b7280", fontSize: 13 }}>
                  Joined: {selectedEmployee.hire_date ? new Date(selectedEmployee.hire_date).toLocaleDateString() : "—"}
                </div>
              </div>

              <div style={{ textAlign: "right" }}>
                <button className="btn" onClick={closeModal}>Close</button>
              </div>
            </div>

            <hr style={{ margin: "12px 0", border: 0, borderTop: "1px solid #eef2f7" }} />

            {empLoading ? (
              <div style={{ padding: 12, color: "#475569" }}>Loading tasks…</div>
            ) : empError ? (
              <div style={{ padding: 12, color: "#9b1c1c", background: "#fff5f5", borderRadius: 8 }}>{empError}</div>
            ) : (
              <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 14 }}>
                <div>
                  <strong>Current Tasks</strong>
                  <div style={{ marginTop: 8 }}>
                    {empTasks?.current && empTasks.current.length > 0 ? (
                      empTasks.current.map((t) => (
                        <div key={t.id || `${t._raw?.task_id}-${Math.random()}`} style={{ padding: 10, borderRadius: 8, background: "#fbfdff", marginBottom: 8 }}>
                          <div style={{ fontWeight: 700 }}>{t.title || t.task_title || `Task ${t.id}`}</div>
                          <div style={{ fontSize: 13, color: "#334155" }}>{t.project_name ? t.project_name : (t._raw?.project_name || "")}</div>
                          <div style={{ fontSize: 12, color: "#6b7280", marginTop: 6 }}>
                            Status: {t.status || t._raw?.status || "—"} · Due: {t.due_date ? new Date(t.due_date).toLocaleDateString() : "—"}
                          </div>
                        </div>
                      ))
                    ) : (
                      <div style={{ color: "#6b7280" }}>No current tasks</div>
                    )}
                  </div>
                </div>

                <div>
                  <strong>Completed Tasks</strong>
                  <div style={{ marginTop: 8 }}>
                    {empTasks?.done && empTasks.done.length > 0 ? (
                      empTasks.done.map((t) => (
                        <div key={t.id || `${t._raw?.task_id}-${Math.random()}`} style={{ padding: 10, borderRadius: 8, background: "#f3f4f6", marginBottom: 8 }}>
                          <div style={{ fontWeight: 700 }}>{t.title || t.task_title || `Task ${t.id}`}</div>
                          <div style={{ fontSize: 13, color: "#334155" }}>{t.project_name ? t.project_name : (t._raw?.project_name || "")}</div>
                          <div style={{ fontSize: 12, color: "#6b7280", marginTop: 6 }}>
                            Completed: {t.updated_at ? new Date(t.updated_at).toLocaleDateString() : (t._raw?.assigned_at ? new Date(t._raw.assigned_at).toLocaleDateString() : "—")}
                          </div>
                        </div>
                      ))
                    ) : (
                      <div style={{ color: "#6b7280" }}>No completed tasks</div>
                    )}
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
